// /pages/HR/components/filters/DateRangeFilter.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import './DateRangeFilter.css';

/**
 * Simple Date Range Filter Component
 * Provides start and end date inputs without presets
 */
const DateRangeFilter = React.memo(({
  startDate = null,
  endDate = null,
  onDateChange = () => {},
  label = '',
  maxDate = new Date(),
  minDate = null,
  placeholder = 'Select dates',
  className = '',
  disabled = false,
  clearable = true,
  showCalendarIcon = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [customStartDate, setCustomStartDate] = useState(startDate);
  const [customEndDate, setCustomEndDate] = useState(endDate);
  const dropdownRef = useRef(null);

  // Format date for display
  const formatDate = useCallback((date) => {
    if (!date) return '';
    const d = new Date(date);
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const year = d.getFullYear();
    return `${month}/${day}/${year}`;
  }, []);

  // Format date for input field
  const formatDateForInput = useCallback((date) => {
    if (!date) return '';
    const d = new Date(date);
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const year = d.getFullYear();
    return `${year}-${month}-${day}`;
  }, []);

  // Get display text for selected range
  const getDisplayText = () => {
    if (startDate && endDate) {
      return `${formatDate(startDate)} - ${formatDate(endDate)}`;
    }
    if (startDate) {
      return `From ${formatDate(startDate)}`;
    }
    if (endDate) {
      return `Until ${formatDate(endDate)}`;
    }
    return placeholder;
  };

  // Handle date change
  const handleDateChange = useCallback((type, value) => {
    const date = value ? new Date(value + 'T00:00:00') : null;

    if (type === 'start') {
      setCustomStartDate(date);
      if (date && customEndDate && date > customEndDate) {
        setCustomEndDate(date);
      }
    } else {
      setCustomEndDate(date);
      if (date && customStartDate && date < customStartDate) {
        setCustomStartDate(date);
      }
    }
  }, [customStartDate, customEndDate]);

  // Apply range
  const applyRange = useCallback(() => {
    onDateChange({
      startDate: customStartDate,
      endDate: customEndDate,
    });
    setIsOpen(false);
  }, [customStartDate, customEndDate, onDateChange]);

  // Clear filter
  const handleClear = useCallback((e) => {
    e.stopPropagation();
    setCustomStartDate(null);
    setCustomEndDate(null);
    onDateChange({
      startDate: null,
      endDate: null,
    });
  }, [onDateChange]);

  // Toggle dropdown
  const toggleDropdown = useCallback(() => {
    if (!disabled) {
      setIsOpen(prev => !prev);
    }
  }, [disabled]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  // Update local state when props change
  useEffect(() => {
    setCustomStartDate(startDate);
    setCustomEndDate(endDate);
  }, [startDate, endDate]);

  const labelId = label ? label.replace(/\s+/g, '-').toLowerCase() : 'date';

  return (
    <div
      ref={dropdownRef}
      className={`date-range-filter ${className} ${isOpen ? 'open' : ''} ${disabled ? 'disabled' : ''}`}
    >
      {/* Filter Trigger */}
      <button
        className="date-range-trigger"
        onClick={toggleDropdown}
        disabled={disabled}
        type="button"
      >
        {showCalendarIcon && (
          <span className="calendar-icon">📅</span>
        )}
        <span className="date-range-text">
          {label ? `${label}: ` : ''}{getDisplayText()}
        </span>
        <span className="dropdown-arrow">▼</span>
      </button>

      {/* Clear Button */}
      {clearable && (startDate || endDate) && !disabled && (
        <button
          className="date-range-clear"
          onClick={handleClear}
          title="Clear date filter"
          type="button"
        >
          ✕
        </button>
      )}

      {/* Dropdown Content */}
      {isOpen && (
        <div className="date-range-dropdown">
          <div className="date-range-custom">
            <div className="custom-input-group">
              <label htmlFor={`start-date-${labelId}`}>Start Date</label>
              <input
                id={`start-date-${labelId}`}
                type="date"
                value={formatDateForInput(customStartDate)}
                onChange={(e) => handleDateChange('start', e.target.value)}
                max={formatDateForInput(maxDate)}
                min={formatDateForInput(minDate)}
              />
            </div>

            <div className="custom-input-group">
              <label htmlFor={`end-date-${labelId}`}>End Date</label>
              <input
                id={`end-date-${labelId}`}
                type="date"
                value={formatDateForInput(customEndDate)}
                onChange={(e) => handleDateChange('end', e.target.value)}
                max={formatDateForInput(maxDate)}
                min={formatDateForInput(customStartDate || minDate)}
              />
            </div>

            <div className="custom-actions">
              <button
                className="apply-button"
                onClick={applyRange}
                type="button"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

DateRangeFilter.displayName = 'DateRangeFilter';

export default DateRangeFilter;
